import { useEffect, useRef } from 'react';
import { useAppContext } from '../context/AppContext';

/**
 * Custom hook that uses IntersectionObserver to track which section
 * is currently in view and updates the active section in context.
 *
 * Uses a MutationObserver to handle sections that render asynchronously
 * (e.g., Certificates that loads data before rendering).
 */
export function useActiveSection(sectionIds = []) {
    const { setActiveSection } = useAppContext();
    const observersRef = useRef([]);

    useEffect(() => {
        const observed = new Set();

        function observeSection(id) {
            if (observed.has(id)) return;
            const element = document.getElementById(id);
            if (!element) return;

            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setActiveSection(id);
                    }
                },
                {
                    rootMargin: '-40% 0px -55% 0px',
                    threshold: 0,
                }
            );

            observer.observe(element);
            observersRef.current.push(observer);
            observed.add(id);
        }

        // Observe all currently-existing sections
        sectionIds.forEach(observeSection);

        // Watch for sections that appear later (async data loading)
        const missingIds = sectionIds.filter((id) => !observed.has(id));
        let mutationObserver = null;

        if (missingIds.length > 0) {
            mutationObserver = new MutationObserver(() => {
                missingIds.forEach(observeSection);
                // Disconnect once all sections are found
                if (missingIds.every((id) => observed.has(id))) {
                    mutationObserver.disconnect();
                }
            });

            mutationObserver.observe(document.body, {
                childList: true,
                subtree: true,
            });
        }

        return () => {
            observersRef.current.forEach((obs) => obs.disconnect());
            observersRef.current = [];
            if (mutationObserver) mutationObserver.disconnect();
        };
    }, [sectionIds, setActiveSection]);
}
